<template>
  <div class="app-shell">
    <aside class="sidebar">
      <div class="sidebar-inner">
        <div class="brand-block">
          <p class="eyebrow">开发常用工具</p>
          <h2 class="brand-title">Dev Toolbox</h2>
          <p class="brand-copy">把常见开发调试动作收进一个顺手、耐看的工具站。</p>
        </div>

        <nav class="side-nav">
          <router-link class="nav-item" active-class="active" exact to="/">
            <span class="nav-item-title">时间戳转换</span>
            <small>秒 / 毫秒互转</small>
          </router-link>
          <router-link class="nav-item" active-class="active" to="/json">
            <span class="nav-item-title">JSON 格式化</span>
            <small>格式化与复制</small>
          </router-link>
          <router-link class="nav-item" active-class="active" to="/translate">
            <span class="nav-item-title">字段翻译</span>
            <small>中英与命名转换</small>
          </router-link>
          <router-link class="nav-item" active-class="active" to="/base64-image">
            <span class="nav-item-title">Base64 预览</span>
            <small>字符串转图片</small>
          </router-link>
        </nav>

        <div class="hero-card sidebar-card">
          <span>Now</span>
          <strong>{{ liveNow }}</strong>
        </div>
      </div>
    </aside>

    <main class="page-shell">
      <header class="hero">
        <div>
          <p class="eyebrow">开发常用工具</p>
          <h1>{{ heroTitle }}</h1>
          <p class="hero-copy">{{ heroCopy }}</p>
        </div>
      </header>

      <router-view />
    </main>
  </div>
</template>

<script>
const routeMetaMap = {
  timestamp: {
    title: "时间戳转换",
    copy: "秒级、毫秒级时间戳与时间字符串独立处理，适合接口联调和日志排查。",
  },
  json: {
    title: "JSON 格式化",
    copy: "专注 JSON 输入、格式化和复制体验，保留开发场景下常见的转义处理选项。",
  },
  translate: {
    title: "字段翻译",
    copy: "接入公开免费翻译接口，适合数据库字段、变量名和简短术语的中英互转与命名整理。",
  },
  base64Image: {
    title: "Base64 图片预览",
    copy: "左侧贴入 Base64 字符串或 Data URL，右侧直接预览图片，方便排查上传和接口返回内容。",
  },
};

const formatInTimeZone = (date) =>
  new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);

export default {
  name: "App",
  data() {
    return {
      liveNow: "--",
      timer: null,
    };
  },
  computed: {
    currentSection() {
      return this.$route.name || "timestamp";
    },
    heroTitle() {
      return routeMetaMap[this.currentSection].title;
    },
    heroCopy() {
      return routeMetaMap[this.currentSection].copy;
    },
  },
  mounted() {
    this.updateLiveClock();
    this.timer = window.setInterval(this.updateLiveClock, 1000);
    window.addEventListener("keydown", this.handleGlobalShortcut);
  },
  beforeDestroy() {
    if (this.timer) {
      window.clearInterval(this.timer);
    }
    window.removeEventListener("keydown", this.handleGlobalShortcut);
  },
  methods: {
    handleGlobalShortcut(event) {
      const isModifierPressed = event.ctrlKey || event.metaKey;
      if (!isModifierPressed) {
        return;
      }

      const targetTag = (event.target && event.target.tagName) || "";
      const isTypingContext =
        targetTag === "INPUT" ||
        targetTag === "TEXTAREA" ||
        (event.target && event.target.isContentEditable);
      if (isTypingContext) {
        return;
      }

      const key = event.key.toLowerCase();
      const routeMap = {
        t: "/",
        j: "/json",
        g: "/translate",
        b: "/base64-image",
      };

      if (!routeMap[key]) {
        return;
      }

      event.preventDefault();
      if (this.$route.path !== routeMap[key]) {
        this.$router.push(routeMap[key]);
      }
    },
    updateLiveClock() {
      const now = new Date();
      this.liveNow = formatInTimeZone(now);
    },
  },
};
</script>

<style>
:root {
  --bg: #f5efe4;
  --bg-accent: #ead8c0;
  --panel: rgba(255, 250, 243, 0.92);
  --line: rgba(74, 56, 33, 0.14);
  --text: #2b2117;
  --muted: #7b6855;
  --brand: #c85c35;
  --brand-deep: #914123;
  --brand-soft: #e8c7b2;
  --brand-faint: rgba(200, 92, 53, 0.12);
  --shadow: 0 24px 60px rgba(93, 59, 22, 0.14);
  --radius-xl: 28px;
  --radius-md: 14px;
  --mono: "SFMono-Regular", "Consolas", "Liberation Mono", monospace;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  min-height: 100vh;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  color: var(--text);
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.78), transparent 28%),
    radial-gradient(circle at bottom right, rgba(200, 92, 53, 0.2), transparent 24%),
    linear-gradient(135deg, var(--bg), var(--bg-accent));
}

body::before {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(43, 33, 23, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(43, 33, 23, 0.03) 1px, transparent 1px);
  background-size: 24px 24px;
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.72), transparent);
}

.app-shell {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  position: sticky;
  top: 0;
  align-self: flex-start;
  width: 320px;
  min-height: 100vh;
  padding: 24px 18px;
}

.sidebar-inner {
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.page-shell {
  position: relative;
  flex: 1;
  max-width: 1320px;
  padding: 40px 24px 56px 0;
}

.brand-block {
  border: 1px solid var(--line);
  background: rgba(255, 250, 243, 0.72);
  box-shadow: 0 18px 40px rgba(93, 59, 22, 0.1);
  backdrop-filter: blur(14px);
  border-radius: var(--radius-xl);
  padding: 22px;
}

.brand-title {
  margin: 0;
  font-size: 2rem;
  line-height: 1;
}

.brand-copy {
  margin: 12px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nav-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 18px;
  border: 1px solid var(--line);
  border-radius: 22px;
  text-decoration: none;
  color: var(--muted);
  background: rgba(255, 250, 243, 0.72);
  box-shadow: 0 12px 30px rgba(93, 59, 22, 0.08);
  backdrop-filter: blur(14px);
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.nav-item:hover {
  transform: translateX(2px);
  color: var(--text);
}

.nav-item.active {
  background: linear-gradient(135deg, rgba(200, 92, 53, 0.94), rgba(145, 65, 35, 0.94));
  color: #fff8f3;
  border-color: rgba(145, 65, 35, 0.4);
}

.nav-item.active small {
  color: rgba(255, 248, 243, 0.78);
}

.nav-item-title {
  font-size: 1rem;
  font-weight: 700;
}

.nav-item small {
  color: var(--muted);
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 28px;
}

.eyebrow {
  margin: 0 0 8px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-size: 12px;
  color: var(--brand-deep);
}

h1 {
  margin: 0;
  font-size: clamp(2.5rem, 5vw, 4.8rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
  max-width: none;
  white-space: nowrap;
}

.hero-copy {
  max-width: none;
  color: var(--muted);
  margin: 18px 0 0;
  font-size: 1rem;
  white-space: nowrap;
}

.hero-card {
  border: 1px solid var(--line);
  background: var(--panel);
  box-shadow: var(--shadow);
  backdrop-filter: blur(18px);
  padding: 22px 24px;
  border-radius: var(--radius-xl);
  display: flex;
  flex-direction: column;
  gap: 8px;
  transform: rotate(1.5deg);
}

.sidebar-card {
  transform: none;
}

.hero-card span,
.hero-card small {
  color: var(--muted);
}

.hero-card strong {
  font-size: 1.5rem;
}

.sidebar-card strong {
  white-space: nowrap;
  font-size: 1.2rem;
  line-height: 1.3;
}

.tool-page {
  max-width: 880px;
}

.panel {
  border: 1px solid var(--line);
  background: var(--panel);
  box-shadow: var(--shadow);
  backdrop-filter: blur(18px);
  border-radius: var(--radius-xl);
}

.panel .el-card__header {
  padding: 24px 24px 0;
  border-bottom: 0;
}

.panel .el-card__body {
  padding: 20px 24px 24px;
}

.panel-head,
.toolbar-inline,
.option-row,
.actions,
.field-row {
  display: flex;
}

.panel-head,
.option-row {
  justify-content: space-between;
  gap: 16px;
}

.panel-head {
  align-items: start;
  margin-bottom: 22px;
}

.toolbar-inline,
.actions,
.field-row {
  gap: 10px;
}

.panel-kicker {
  margin: 0 0 8px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-size: 12px;
  color: var(--brand-deep);
}

h2 {
  margin: 0;
  font-size: 1.8rem;
  line-height: 1.1;
}

.full-width,
.full-width .el-date-editor,
.field-row .el-input {
  width: 100%;
}

.el-button {
  border-radius: 999px;
}

.el-button--primary {
  background-color: var(--brand);
  border-color: var(--brand);
  color: #fff8f3;
}

.el-button--primary:hover,
.el-button--primary:focus {
  background-color: #d46d47;
  border-color: #d46d47;
  color: #fff8f3;
}

.el-button.is-plain,
.el-button--default {
  background: rgba(255, 255, 255, 0.72);
  border-color: rgba(74, 56, 33, 0.14);
  color: var(--text);
}

.el-button:hover,
.el-button:focus,
.el-button--default:hover,
.el-button--default:focus,
.el-button.is-plain:hover,
.el-button.is-plain:focus {
  color: var(--brand-deep);
  border-color: var(--brand-soft);
  background: rgba(255, 250, 243, 0.94);
}

.el-button--primary:hover,
.el-button--primary:focus {
  color: #fff8f3;
  background-color: #d46d47;
}

.el-button--text {
  color: var(--brand);
}

.el-button--text:hover,
.el-button--text:focus {
  color: var(--brand-deep);
}

.el-input__inner,
.el-input-number,
.el-date-editor.el-input,
.el-textarea__inner {
  border-radius: var(--radius-md);
  border-color: rgba(74, 56, 33, 0.16);
  background: rgba(255, 255, 255, 0.8);
  color: var(--text);
}

.el-input__inner:focus,
.el-date-editor .el-input__inner:focus,
.el-textarea__inner:focus {
  border-color: rgba(200, 92, 53, 0.7);
  box-shadow: 0 0 0 3px var(--brand-faint);
}

.el-input.is-active .el-input__inner,
.el-input__inner:hover,
.el-textarea__inner:hover {
  border-color: rgba(200, 92, 53, 0.34);
}

.el-date-editor .el-range-input,
.el-date-editor .el-input__inner,
.el-date-editor .el-input__prefix,
.el-date-editor .el-input__suffix {
  color: var(--text);
}

.el-input__prefix,
.el-input__suffix,
.el-date-table th,
.el-picker-panel__icon-btn {
  color: var(--muted);
}

.el-input__prefix:hover,
.el-input__suffix:hover,
.el-picker-panel__icon-btn:hover {
  color: var(--brand);
}

.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover,
.el-autocomplete-suggestion li:hover {
  background-color: rgba(200, 92, 53, 0.08);
}

.el-select-dropdown__item.selected {
  color: var(--brand-deep);
}

.el-textarea__inner {
  min-height: 180px;
  line-height: 1.6;
  font-family: var(--mono);
}

.el-form-item__label,
.el-checkbox {
  color: var(--muted);
}

.el-form-item.is-error .el-input__inner,
.el-form-item.is-error .el-textarea__inner {
  border-color: #af2d2d;
}

.el-checkbox {
  line-height: 1.5;
}

.el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--text);
}

.el-checkbox__inner:hover,
.el-radio__inner:hover {
  border-color: var(--brand);
}

.el-checkbox__inner,
.el-radio__inner {
  border-color: rgba(74, 56, 33, 0.22);
  background-color: rgba(255, 255, 255, 0.9);
}

.el-checkbox__input.is-focus .el-checkbox__inner,
.el-checkbox__input:not(.is-checked):not(.is-indeterminate) .el-checkbox__inner:focus,
.el-checkbox__original:focus + .el-checkbox__inner,
.el-radio__input.is-focus .el-radio__inner,
.el-radio__original:focus + .el-radio__inner {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-faint);
}

.el-checkbox__input:not(.is-checked):not(.is-indeterminate) .el-checkbox__inner::after {
  border-color: transparent;
}

.el-checkbox__input.is-checked .el-checkbox__inner,
.el-checkbox__input.is-indeterminate .el-checkbox__inner,
.el-radio__input.is-checked .el-radio__inner {
  background-color: var(--brand);
  border-color: var(--brand);
}

.el-radio__input.is-checked + .el-radio__label {
  color: var(--brand-deep);
}

.el-date-table td.current:not(.disabled) span,
.el-date-table td.available:hover,
.el-date-table td.today span,
.el-month-table td.current:not(.disabled) .cell,
.el-month-table td .cell:hover,
.el-year-table td .cell:hover,
.el-year-table td.current:not(.disabled) .cell {
  color: var(--brand-deep);
}

.el-date-table td.available:hover span,
.el-month-table td .cell:hover,
.el-year-table td .cell:hover {
  background-color: var(--brand-faint);
  border-radius: 10px;
}

.el-date-table td.current:not(.disabled) span,
.el-month-table td.current:not(.disabled) .cell,
.el-year-table td.current:not(.disabled) .cell {
  background-color: var(--brand);
  color: #fff8f3;
  border-radius: 10px;
}

.el-date-picker__header-label,
.el-date-picker__header-label:hover,
.el-picker-panel__shortcut:hover,
.el-time-panel__btn.confirm {
  color: var(--brand-deep);
}

.el-date-picker__time-header,
.el-date-picker__header--bordered,
.el-picker-panel__footer {
  border-color: rgba(74, 56, 33, 0.12);
}

.el-time-spinner__item:hover:not(.disabled):not(.active),
.el-time-panel__content::after,
.el-time-panel__content::before {
  background: rgba(200, 92, 53, 0.08);
}

.el-time-spinner__item.active:not(.disabled) {
  color: var(--brand-deep);
}

.el-date-table td.available:hover,
.el-month-table td .cell:hover,
.el-year-table td .cell:hover,
.el-date-picker__header-label.active,
.el-picker-panel__shortcut.active {
  color: var(--brand-deep);
}

.el-picker-panel,
.el-select-dropdown,
.el-autocomplete-suggestion {
  border-color: rgba(74, 56, 33, 0.12);
}

.option-row {
  align-items: center;
  flex-wrap: wrap;
  margin: 8px 0 18px;
}

.actions {
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.status {
  min-height: 24px;
  margin: 0;
  color: var(--muted);
}

.status.error {
  color: #af2d2d;
}

.status.success {
  color: #2f7a4f;
}

@media (max-width: 960px) {
  .app-shell {
    flex-direction: column;
  }

  .sidebar {
    position: static;
    width: auto;
    min-height: auto;
    padding: 18px 14px 0;
  }

  .sidebar-inner {
    position: static;
  }

  .page-shell {
    max-width: none;
    padding: 18px 14px 40px;
  }
}

@media (max-width: 640px) {
  .brand-title {
    font-size: 1.6rem;
  }

  .panel .el-card__header {
    padding: 18px 18px 0;
  }

  .panel .el-card__body {
    padding: 16px 18px 18px;
  }

  .panel-head,
  .option-row,
  .field-row {
    flex-direction: column;
  }

  .el-button {
    width: 100%;
  }
}
</style>
